import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Users, BookOpen, ShieldOff } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator } from '../../../mascot';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import type { Classroom } from '../../../accounts';
interface CoTeacherWelcomeModalProps {
  open: boolean;
  classroom: Classroom | null;
  onClose: () => void;
}
/**
 * One-time welcome shown after a teacher accepts a co-teacher invite.
 *
 * Djay-zone: YES (Zone 1 — first-run / celebration).
 * Wave 3.3 — Co-teacher accept flow.
 */
export function CoTeacherWelcomeModal({
  open,
  classroom,
  onClose
}: CoTeacherWelcomeModalProps) {
  useEscapeKey(onClose, open && classroom !== null);
  return <AnimatePresence data-id="element-8307">
      {open && classroom && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="co-teacher-welcome-title" data-id="element-8308">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-8309" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-8310">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-indigo-400 to-amber-400" data-id="element-8311" />

            <div className="p-8 text-center" data-id="element-8312">
              <div className="flex justify-center mb-4" data-id="element-8313">
                <div className="relative" data-id="element-8314">
                  <DjayNarrator size="md" emotion="celebrating" data-id="element-8315" />
                  <Sparkles className="absolute -top-1 -right-2 w-5 h-5 text-amber-400" data-id="element-8316" />
                </div>
              </div>

              <h2 id="co-teacher-welcome-title" className="text-2xl font-black text-slate-900 mb-2" data-id="element-8317">
                Welkom in {classroom.name}!
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-6" data-id="element-8318">
                Je bent toegevoegd als{' '}
                <span className="font-bold" data-id="element-8319">co-docent</span>. Hieronder zie je
                wat je kunt doen.
              </p>

              <ul className="text-left space-y-3 mb-6" data-id="element-8320">
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-8321">
                  <Users className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-8322" />
                  <div data-id="element-8323">
                    <p className="text-sm font-bold text-slate-900" data-id="element-8324">
                      Leerlingen begeleiden
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-8325">
                      Je ziet alle leerlingen, hun voortgang en kunt leerlingen
                      toevoegen of verwijderen.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-8326">
                  <BookOpen className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-8327" />
                  <div data-id="element-8328">
                    <p className="text-sm font-bold text-slate-900" data-id="element-8329">
                      Huiswerk &amp; toetsen
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-8330">
                      Je kunt zelfstandig huiswerk en toetsen klaarzetten voor
                      deze klas.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-8331">
                  <ShieldOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8332" />
                  <div data-id="element-8333">
                    <p className="text-sm font-bold text-amber-900" data-id="element-8334">
                      Klas-structuur blijft bij eigenaar
                    </p>
                    <p className="text-xs text-amber-800" data-id="element-8335">
                      Alleen de klas-eigenaar kan de klas hernoemen, verwijderen
                      of andere co-docenten beheren.
                    </p>
                  </div>
                </li>
              </ul>

              <Button onClick={onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all" data-id="element-8336">
                Aan de slag
              </Button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}